import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from '@components/Tooltip';
import { useTranslation, useChannels } from '@hooks';
import { contactsActions } from '@actions';
import { ModalType } from '@constants/modal';
import { ConversationIcon, MergeContactsIcon, DeleteIcon } from 'assets/images/icons';
import Info from './Info';
import Activity from './Activity';
import { RadioButtonsWrapper, RadioButtons, StyledIconButton } from './styles';

const Tab = {
    Information: 0,
    Activity: 1,
};

const ContactInfo = ({ contact, resizable, setActiveConversation = null }) => {
    const [tab, setTab] = useState(Tab.Information);

    const companies = useSelector(state => state.account?.account?.companies || []);

    const { getChannelsByLocationsIds, checkTwoWaySms, checkWhatsAppStatus, checkTwoWayLiveChat } = useChannels();
    const isSmallScreen = useMediaQuery('@media (min-width: 1025px) and (max-width: 1280px)');
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const onOpenDeleteModal = () => dispatch(contactsActions.setContactDeleteModal(true));

    const onOpenMergeModal = () => dispatch(contactsActions.setContactMergeModal(true));

    const onOpenEditModal = () => dispatch(contactsActions.setContactEditModal({ open: true, type: ModalType.Edit }));

    const onOpenInitConversation = () => {
        dispatch(
            contactsActions.setContactInitConversationModal({
                open: true,
                locations: contact?.companies,
                contactId: contact?.id,
                ctx: contact,
            }),
        );
    };

    const availableChannels = useMemo(() => {
        if (!contact?.companies) return [];
        const filterCb = c => checkTwoWaySms(c) || checkWhatsAppStatus(c) || checkTwoWayLiveChat(c);
        return getChannelsByLocationsIds(contact?.companies, filterCb);
    }, [contact]);

    const isDisabled = !contact;

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Tooltip title={t('InitConversation.newConversation')} placement="top">
                    <span>
                        <StyledIconButton
                            disabled={isDisabled || availableChannels.length === 0}
                            onClick={onOpenInitConversation}
                        >
                            <ConversationIcon />
                        </StyledIconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('common.edit')} placement="top">
                    <span>
                        <StyledIconButton onClick={onOpenEditModal} disabled={isDisabled}>
                            <EditIcon viewBox="0 0 22 22" />
                        </StyledIconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('buttons.merge')} placement="top">
                    <span>
                        <StyledIconButton onClick={onOpenMergeModal} disabled={isDisabled}>
                            <MergeContactsIcon />
                        </StyledIconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('common.delete')} placement="top">
                    <span>
                        <StyledIconButton onClick={onOpenDeleteModal} disabled={isDisabled}>
                            <DeleteIcon />
                        </StyledIconButton>
                    </span>
                </Tooltip>
            </div>
            <div style={{ height: '1px', backgroundColor: 'rgba(0, 0, 0, 0.12)', margin: '16px 0' }} />
            <RadioButtonsWrapper>
                <RadioButtons
                    value={tab}
                    onChange={setTab}
                    options={[
                        { value: Tab.Information, label: t('common.information') },
                        { value: Tab.Activity, label: t('common.activity') },
                    ]}
                />
            </RadioButtonsWrapper>
            {tab === Tab.Information && (
                <Info contact={contact} isSmallScreen={isSmallScreen} resizable={resizable} companies={companies} />
            )}
            {tab === Tab.Activity && (
                <Activity
                    contact={contact}
                    isSmallScreen={isSmallScreen}
                    resizable={resizable}
                    setActiveConversation={setActiveConversation}
                />
            )}
        </>
    );
};

export default ContactInfo;
